import { Table, Tag, Spin } from "antd";
import type { ColumnsType } from "antd/es/table";
import { filesize } from "filesize";
import { useState } from "react";
import PageLayout from "../../../layouts/pageLayout/pageLayout";
import ProgramInfo from "../../../components/programInfo";
import { useGetStudentUploadedDocumentsQuery } from "../../../hooks/api/useGetStudentUploadedDocumentsQuery";
import { useReUploadDocumentMutation } from "../../../hooks/api/useReUploadDocumentMutation";
import { formatFullDate, formattedTime } from "../../../utils/formatDate";
import styles from "../dashboardPage.module.scss";

interface RequiredDocumentTableRow extends StudentUploadedDocument {
  key: number;
}

const RequiredDocuments = () => {
  const { documents, isLoading, isError, error, refetch } =
    useGetStudentUploadedDocumentsQuery();
  const { reUploadHandler, isLoading: isReUploading } =
    useReUploadDocumentMutation();
  const [uploadingDocumentId, setUploadingDocumentId] = useState<number | null>(
    null,
  );

  const tableData: RequiredDocumentTableRow[] =
    documents?.map((document) => ({
      ...document,
      key: document.id,
    })) ?? [];

  const isRejectedDocument = (document: StudentUploadedDocument) =>
    document.verificationStatus?.toLowerCase().includes("reject") ||
    !!document.rejectionReason;

  const handleReUpload =
    (record: RequiredDocumentTableRow) =>
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];

      if (!file) {
        return;
      }

      try {
        setUploadingDocumentId(record.id);
        await reUploadHandler({
          studentDocumentId: record.id,
          file,
        });
        await refetch();
      } finally {
        setUploadingDocumentId(null);
        event.target.value = "";
      }
    };

  const columns: ColumnsType<RequiredDocumentTableRow> = [
    {
      title: "S/N",
      key: "serialNumber",
      render: (_, __, index) => index + 1,
    },
    {
      title: "Document Name",
      dataIndex: "requiredDocumentName",
      key: "requiredDocumentName",
    },
    {
      title: "File Name",
      dataIndex: "fileName",
      key: "fileName",
      render: (value: string) => value || "N/A",
    },
    {
      title: "File Size",
      dataIndex: "fileSizeInMB",
      key: "fileSizeInMB",
      render: (value: number) =>
        value ? filesize(value, { base: 10, round: 2 }) : "N/A",
    },
    {
      title: "Uploaded Date",
      dataIndex: "uploadedDate",
      key: "uploadedDate",
      render: (value: string) =>
        value ? (
          <div>
            <p>{formatFullDate(value)}</p>
            <p>{formattedTime(value)}</p>
          </div>
        ) : (
          "N/A"
        ),
    },
    {
      title: "Verification",
      key: "verificationStatus",
      render: (_, record) => (
        <Tag
          color={
            isRejectedDocument(record)
              ? "red"
              : record.isVerified
                ? "green"
                : "default"
          }
        >
          {isRejectedDocument(record)
            ? "Rejected"
            : record.isVerified
              ? "Verified"
              : "Pending"}
        </Tag>
      ),
    },
    {
      title: "Comment",
      dataIndex: "rejectionReason",
      key: "rejectionReason",
      render: (value: string) => value || "N/A",
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          {record.fileUrl ? (
            <a href={record.fileUrl} target="_blank" rel="noopener noreferrer">
              View Document
            </a>
          ) : (
            <span>N/A</span>
          )}

          {isRejectedDocument(record) && (
            <label
              style={{
                color: "#1677ff",
                cursor:
                  isReUploading && uploadingDocumentId === record.id
                    ? "not-allowed"
                    : "pointer",
              }}
            >
              {isReUploading && uploadingDocumentId === record.id
                ? "Re-uploading..."
                : "Re-upload"}
              <input
                type="file"
                hidden
                onChange={handleReUpload(record)}
                disabled={isReUploading && uploadingDocumentId === record.id}
              />
            </label>
          )}
        </div>
      ),
    },
  ];

  if (isLoading) {
    return <Spin size="large" />;
  }

  if (isError) {
    return <div>{error?.message}</div>;
  }

  return (
    <PageLayout
      heading="Required Document"
      paragraph="View the documents you have uploaded for your application."
    >
      <ProgramInfo className={styles.rectangle} />
      <br />
      <Table
        columns={columns}
        dataSource={tableData}
        className={styles.row}
        rowKey="key"
        scroll={{ x: 900 }}
        pagination={false}
        locale={{
          emptyText: "No uploaded documents found for this application.",
        }}
      />
    </PageLayout>
  );
};

export default RequiredDocuments;
